import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;

    this.renderCartBubble(itemCount);
  };

  /**
   * Renders the cart bubble.
   *
   * R57c-4: `itemCount` is now ALWAYS the cart's absolute item count, never a
   * delta. Stock branched on `source === 'product-form-component'` and added
   * that source's count to the number already on screen, because the product
   * form was the one initiator reporting "how many I just added" rather than
   * "how many are in the cart". R57c routed every initiator through
   * cartController, whose applyMutation reads the absolute count out of the
   * rendered cart section, so the delta branch had exactly one caller left -
   * and routing the product form through the controller WITHOUT removing it
   * would have made the badge double-count every PDP add. Deleting it ends the
   * whole class of bug: a new initiator can no longer pick the wrong
   * arithmetic by choosing the wrong source string.
   *
   * It also kills a latent one. The `currentCartCount` setter blanks the text
   * node at 100+, and the getter parsed that blank back as NaN, so on a cart of
   * a hundred items the old increment produced NaN and the badge showed nothing
   * at all until a reload.
   * @param {number} itemCount - The number of items in the cart.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    this.#syncAccessibleLabels(itemCount);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * R57b-6: the header is no longer morphed on cart mutations (the drawer
   * moved to its own section, and cart requests stopped returning the header
   * section), so the two server-rendered accessible texts - the trigger's
   * aria-label and the bubble's visually hidden count - are refreshed here,
   * in the same authoritative path as the visible count. Both texts end in
   * the count, so the locale-translated prefix is preserved as rendered.
   * @param {number} count - The post-update cart item count.
   */
  #syncAccessibleLabels(count) {
    if (!Number.isFinite(count)) return;

    const trigger = this.closest('[aria-label]');
    if (trigger) {
      const label = trigger.getAttribute('aria-label') || '';
      if (/\d+\s*$/.test(label)) {
        trigger.setAttribute('aria-label', label.replace(/\d+\s*$/, String(count)));
      }
    }

    const hiddenText = this.refs.cartBubbleText?.querySelector('.visually-hidden');
    if (hiddenText && /\d+\s*$/.test(hiddenText.textContent || '')) {
      hiddenText.textContent = (hiddenText.textContent || '').replace(/\d+\s*$/, String(count));
    }
  }

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
